// buildSettingsPayload — pure translator from onboarding wizard state to the
// tenant settings PUT payload.
//
// Revised 2026-08-17 for IDI-62 (318_ §3.1). The rule this module enforces is
// unchanged from the first pass, but the mechanism is not:
//
//   OLD (WRONG)   "if the field is undefined/null/empty, omit it"
//   NEW (RIGHT)   "if the field equals its canonical initial value, omit it"
//
// The canonical initial value lives in onboardingInitialState.js. That is the
// only definition of "the user did not fill this in." A migrated tenant whose
// masterDefaultMargin is 0.12 must not have it overwritten by the wizard's
// default of 5% — even though 5% is a perfectly-defined value the user did
// not choose. The old mechanism did not catch that: it saw `5` as "defined"
// and sent `0.05`.
//
// Backend contract this closes over:
//   - PUT /v1/settings does a TOP-LEVEL KEY REPLACE (settings/main.go:910).
//   - PUT with a nested sub-object replaces the WHOLE sub-object
//     (settings/main.go:596-604). So a partial `branding` object with only
//     `brandColor` set would erase `logo`, `secondaryColor`, `textColor`,
//     etc. To prevent that we merge user-set fields OVER the tenant's
//     currentSettings[key] before we send.
//
// Rules:
//   1. NEVER include a key the user did not touch (touched === !equal(initial)).
//   2. When a sub-object key IS included, merge user's touched fields OVER
//      the tenant's currentSettings[key] so untouched siblings survive.
//   3. `undefined`, not `null`, everywhere. The top-level strip is a
//      guardrail against bugs in the guards, not the guard itself.

import { INITIAL_WIZARD_DATA } from './onboardingInitialState';
import { serialiseLaborRates } from '../../quote-builder/laborMethods';

// ─── Equality / touched ──────────────────────────────────────────────────────

/** Deep, structural equality — sufficient for the plain data the wizard holds
 *  (primitives, plain objects, arrays). Not designed for Dates, Maps, class
 *  instances, or cyclic graphs. */
function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (!deepEqual(a[k], b[k])) return false;
  return true;
}

/** touched — did the user change this field from its canonical initial value? */
function touched(wizardData, key) {
  return !deepEqual(wizardData[key], INITIAL_WIZARD_DATA[key]);
}

/** pick — return the user's value for `key`, or undefined if they didn't
 *  touch it. This is the ONLY way this module extracts a field from
 *  wizardData for wire-emission. */
function pick(wizardData, key) {
  return touched(wizardData, key) ? wizardData[key] : undefined;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Compact — drop entries whose value is undefined. Returns undefined if none
 *  survive, so the caller's top-level strip removes the whole key. */
function compact(obj) {
  if (obj == null || typeof obj !== 'object') return undefined;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/** withActive — normalise a list of strings or objects into
 *  { name, active: true, ...rest } shape. Preserves an explicit `active`. */
function withActive(arr, extra = {}) {
  if (!Array.isArray(arr) || arr.length === 0) return undefined;
  return arr.map((it) =>
    typeof it === 'string'
      ? { name: it, active: true, ...extra }
      : { active: true, ...it, ...extra }
  );
}

/** mergeSubObject — spread the compacted user fields OVER currentSettings[key].
 *  Returns undefined when the user touched nothing in this section, so the
 *  key is omitted from the payload entirely and the backend never rewrites it. */
function mergeSubObject(currentSubObj, userFields) {
  const compacted = compact(userFields);
  if (compacted === undefined) return undefined;
  const base = (currentSubObj && typeof currentSubObj === 'object') ? currentSubObj : {};
  return { ...base, ...compacted };
}

// Same rule as CompanyProfilePage.js normalizeWebsite (IDI-654 D1).
function normalizeWebsite(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** IDI-397 D1/D3: Step 3 merges into Settings' marginDefaults record (the
 *  PUT replaces it whole, so we start from the stored one). Only changes from
 *  what the step showed (`wd.marginsShown`, else the initial values) are
 *  applied; nothing goes top-level. A dealer with no default stored anywhere
 *  gets 0.05 even if Step 3 was untouched or skipped. Returns undefined when
 *  there is nothing to write. */
function buildMarginDefaults(wd, cur) {
  const shown = wd.marginsShown || {};
  const md = { ...(isPlainObject(cur.marginDefaults) ? cur.marginDefaults : {}) };
  let changed = false;

  const shownCats = shown.categories || INITIAL_WIZARD_DATA.categories || [];
  const shownMargin = {};
  // Settings' key is "bats"; progress saved before IDI-397 carries "batts".
  const keyOf = (id) => (id === 'batts' ? 'bats' : id);
  for (const c of shownCats) shownMargin[keyOf(c.id)] = c.margin;
  const catChanges = {};
  for (const c of Array.isArray(wd.categories) ? wd.categories : []) {
    const before = shownMargin[keyOf(c.id)] ?? null;
    const after = c.margin ?? null;
    if (after !== before && !(after === '' && before == null)) catChanges[keyOf(c.id)] = after ?? '';
  }
  if (Object.keys(catChanges).length) {
    md.category_margins = { ...(isPlainObject(md.category_margins) ? md.category_margins : {}), ...catChanges };
    changed = true;
  }

  const shownLabor = shown.laborRates || INITIAL_WIZARD_DATA.laborRates;
  if (wd.laborRates !== undefined && !deepEqual(wd.laborRates, shownLabor)) {
    md.labor_rates = serialiseLaborRates(wd.laborRates);
    changed = true;
  }

  const shownDefault = shown.masterDefaultMargin ?? INITIAL_WIZARD_DATA.masterDefaultMargin;
  const typed = wd.masterDefaultMargin === '' || wd.masterDefaultMargin == null
    ? NaN : Number(wd.masterDefaultMargin);
  if (Number.isFinite(typed) && typed !== Number(shownDefault)) {
    md.master_default_margin = Math.round(typed * 100) / 10000;
    changed = true;
  }

  const stored = (isPlainObject(cur.marginDefaults) ? cur.marginDefaults.master_default_margin : null)
    ?? cur.master_default_margin;
  if (stored == null && md.master_default_margin == null) {
    md.master_default_margin = 0.05;
    changed = true;
  }
  return changed ? md : undefined;
}

/** IDI-666 D1/D4: Quote Defaults with the dealer's picked term, merged over
 *  the stored object (the PUT replaces quoteDefaults whole). Undefined when
 *  nothing was picked or the pick is the stored term. */
function buildQuoteDefaults(wd, cur) {
  const term = wd.paymentTermPick;
  const qd = isPlainObject(cur.quoteDefaults) ? cur.quoteDefaults : {};
  if (typeof term !== 'string' || !term || term === qd.payment_terms) return undefined;
  return { ...qd, payment_terms: term };
}

/** IDI-655 D1-D4: the whole taxRates list — the rows stored at finish with
 *  setup's typed changes. Edited stored rows change only their rate; an added
 *  state already stored (even since the pre-fill) updates that row; others
 *  are appended. Nothing is deleted. Undefined when nothing was typed (D5). */
function buildTaxRates(wd, cur) {
  const ch = isPlainObject(wd.taxRateEdits) ? wd.taxRateEdits : {};
  const edits = isPlainObject(ch.edits) ? ch.edits : {};
  const added = Array.isArray(ch.added) ? ch.added : [];
  if (Object.keys(edits).length === 0 && added.length === 0) return undefined;
  const st = (r) => String((r && r.state) || '').trim().toUpperCase();
  const rows = (Array.isArray(cur.taxRates) ? cur.taxRates : []).map((r) => (
    r && r.id != null && Object.prototype.hasOwnProperty.call(edits, r.id) ? { ...r, rate: edits[r.id] } : r
  ));
  const ids = new Set(rows.map((r) => r && r.id));
  let n = Date.now();
  let first = -1;
  for (const a of added) {
    const state = st(a);
    const i = rows.findIndex((r) => st(r) === state);
    if (i >= 0) { rows[i] = { ...rows[i], rate: a.rate }; if (first < 0) first = i; continue; }
    while (ids.has(`tax_${n}`)) n += 1;
    ids.add(`tax_${n}`);
    const row = { id: `tax_${n}`, name: `${state} Sales Tax`, state, rate: a.rate, exempt_types: [], isDefault: false };
    rows.push(row);
    if (first < 0) first = rows.length - 1;
  }
  // IDI-702 D1-D3: with no default yet, Your Business's state's row (stored
  // or added) becomes it; else the first state added. Only that row changes.
  if (first >= 0 && !rows.some((r) => r && (r.isDefault || r.is_default))) {
    const home = st(wd.address);
    const h = home ? rows.findIndex((r) => st(r) === home) : -1;
    const idx = h >= 0 ? h : first;
    rows[idx] = { ...rows[idx], isDefault: true };
  }
  return rows;
}

/** IDI-626 D1/D2/D6: the whole tcTemplates list — the entries stored at
 *  finish with setup's changes. Edits change name, content and
 *  customer_types only; deletes drop by id; adds are appended as
 *  create_tc_template stores them unless that id is already stored.
 *  Undefined when nothing differs from the stored list. */
function buildTcTemplates(wd, cur) {
  const ch = isPlainObject(wd.tcChanges) ? wd.tcChanges : {};
  const edits = isPlainObject(ch.edits) ? ch.edits : {};
  const deleted = new Set(Array.isArray(ch.deleted) ? ch.deleted : []);
  const added = Array.isArray(ch.added) ? ch.added : [];
  const before = Array.isArray(cur.tcTemplates) ? cur.tcTemplates : [];
  const has = (id) => Object.prototype.hasOwnProperty.call(edits, id);
  const list = before.filter((t) => !(t && deleted.has(t.id))).map((t) => {
    if (!t || !has(t.id)) return t;
    const { name, content, customer_types } = edits[t.id];
    return { ...t, name, content, customer_types };
  });
  const ids = new Set(before.map((t) => t && t.id));
  for (const a of added) {
    if (!a || ids.has(a.id)) continue;
    ids.add(a.id);
    list.push({ id: String(a.id), name: a.name, content: a.content || '',
      customer_types: Array.isArray(a.customer_types) ? a.customer_types : [], active: true });
  }
  return deepEqual(list, before) ? undefined : list;
}

// ─── Main ────────────────────────────────────────────────────────────────────

/**
 * Build the settings PUT payload from wizard state.
 *
 * @param {object} wizardData         Onboarding wizard state.
 * @param {object} [currentSettings]  Tenant's CURRENT settings, fetched via
 *                                    settingsAPI.getSettings() before the PUT.
 *                                    Used to preserve untouched fields inside
 *                                    sub-objects (branding, companyProfile,
 *                                    quoteDefaults) which the backend
 *                                    wholesale-replaces when present.
 * @returns {object} Payload safe to pass to settingsAPI.updateSettings().
 */
export function buildSettingsPayload(wizardData, currentSettings = {}) {
  const wd = wizardData || {};
  const cur = currentSettings || {};

  // Every field routes through pick(wd, key). Untouched fields become
  // undefined and are stripped at the bottom.
  // IDI-654 D1: Company Profile's keys only; About You's phone is the user's
  // (D2); subdomain is never sent (D4).
  const addr = pick(wd, 'address') || {};
  const addrPart = (k) => (addr[k] ? addr[k] : undefined);
  const website = pick(wd, 'website');
  const companyProfileFields = {
    company_name: pick(wd, 'companyName'),
    phone: pick(wd, 'businessPhone'),
    email: pick(wd, 'businessEmail'),
    address_street: addrPart('street'),
    address_city: addrPart('city'),
    address_state: addrPart('state'),
    address_zip: addrPart('zip'),
    website: website === undefined ? undefined : normalizeWebsite(website) || undefined,
  };

  // IDI-656 D2: branding is saved at Your Business's Continue
  // (cmd/onboarding), so the finish saves none.

  // IDI-657 D3: crews and Resource Types rows are added one at a time through
  // Settings' add_list_item at the finish, so the PUT carries neither.

  const payload = {
    companyProfile: mergeSubObject(cur.companyProfile, companyProfileFields),
    marginDefaults: buildMarginDefaults(wd, cur),
    taxRates: buildTaxRates(wd, cur),
    quoteDefaults: buildQuoteDefaults(wd, cur),
    // IDI-626: setup never writes the tcTemplate string.
    tcTemplates: buildTcTemplates(wd, cur),
  };

  // Guardrail strip. Nothing above should produce a non-undefined value for
  // an untouched field; if that ever happens it is a bug in a guard, not a
  // rescue this strip is meant to perform.
  for (const k of Object.keys(payload)) {
    if (payload[k] === undefined) delete payload[k];
  }
  return payload;
}

// Exported for tests only.
export const __test = { compact, withActive, mergeSubObject, deepEqual, touched, pick };
